import React, { createContext, useCallback, useContext, useRef, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { useI18n } from "../../i18n/LanguageContext";

const ConfirmContext = createContext(() => Promise.resolve(false));

// Design-system confirmation modal (replaces window.confirm everywhere).
// Usage: const confirm = useConfirm(); if (!(await confirm({ message }))) return;
export function ConfirmProvider({ children }) {
  const { t } = useI18n();
  const [state, setState] = useState(null);
  const resolver = useRef(null);

  const confirm = useCallback((opts = {}) => new Promise((resolve) => {
    resolver.current = resolve;
    setState(opts);
  }), []);

  const close = (result) => {
    resolver.current?.(result);
    resolver.current = null;
    setState(null);
  };

  return (
    <ConfirmContext.Provider value={confirm}>
      {children}
      {state && (
        <div data-testid="confirm-dialog-overlay" onClick={() => close(false)}
          className="fixed inset-0 z-[200] flex items-center justify-center bg-black/40 p-6 backdrop-blur-sm">
          <div data-testid="confirm-dialog" onClick={(e) => e.stopPropagation()}
            className="w-full max-w-sm rounded-2xl border border-black/8 bg-white p-6 shadow-[0_24px_80px_rgba(0,0,0,0.3)] duration-200 animate-in fade-in-0 zoom-in-95">
            <div className="mb-4 flex h-10 w-10 items-center justify-center rounded-xl bg-[#b4443a]/10 text-[#b4443a]">
              <AlertTriangle size={18} />
            </div>
            <h3 className="font-display text-lg font-light tracking-tight text-[#050505]">
              {state.title || t("admin.confirmTitle")}
            </h3>
            <p className="mt-1.5 text-sm font-light leading-relaxed text-[#6b6f6c]" data-testid="confirm-dialog-message">
              {state.message}
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button data-testid="confirm-cancel-btn" onClick={() => close(false)}
                className="rounded-lg border border-black/12 px-4 py-2 text-xs font-medium text-[#141514] transition-colors hover:bg-black/[0.03]">
                {state.cancelLabel || t("admin.confirmCancel")}
              </button>
              <button data-testid="confirm-accept-btn" onClick={() => close(true)} autoFocus
                className="rounded-lg bg-[#b4443a] px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-90">
                {state.confirmLabel || t("admin.confirmDelete")}
              </button>
            </div>
          </div>
        </div>
      )}
    </ConfirmContext.Provider>
  );
}

export function useConfirm() {
  return useContext(ConfirmContext);
}
